import { useCallback, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import type { CustomizationService } from '@/features/customization/application/services/CustomizationService';
import type { ProductCustomization } from '@/features/customization/domain/models/ProductCustomization';
import type { PdpSizeOption } from '../viewModels/pdp.vm';
import type { EarlyDeliveryItem } from '../../domain/models/EarlyDeliveryItem';
import { EARLY_DELIVERY_PERIOD_DAYS } from '../../domain/models/EarlyDeliveryItem';
import {
  earlyDeliveryItemToSelection,
  formatEarlyDeliveryByLabel,
  formatEarlyDeliveryDiamondLabel,
  formatEarlyDeliveryMetalLabel,
  formatEarlyDeliverySolitaireLabel,
} from '../../utils/earlyDeliveryItem.utils';
import { cn } from '@/shared/utils/cn';

interface PdpEarlyDeliveryProps {
  items: EarlyDeliveryItem[];
  designId: string;
  service: CustomizationService;
  onApplied: (result: {
    customization: ProductCustomization;
    selectedSize: PdpSizeOption | null;
    item: EarlyDeliveryItem;
  }) => void;
}

function EarlyDeliveryItemRow({
  item,
  isApplying,
  onViewDetails,
}: {
  item: EarlyDeliveryItem;
  isApplying: boolean;
  onViewDetails: (item: EarlyDeliveryItem) => void;
}) {
  const metalLabel = formatEarlyDeliveryMetalLabel(item);
  const diamondLabel = formatEarlyDeliveryDiamondLabel(item);
  const solitaireLabel = formatEarlyDeliverySolitaireLabel(item);
  const showSize = item.size.size !== '00';

  return (
    <div className="border-t border-[#e3e3e3] px-[15px] py-2.5 text-left text-[13px] leading-[19px] text-[#001e38]">
      <p className="font-semibold">
        {metalLabel}
        {diamondLabel ? ` | ${diamondLabel}` : ''}
        {solitaireLabel ? ` | ${solitaireLabel}` : ''}
        {showSize ? ` | Size ${item.size.displaySize}` : ''}
      </p>
      <div className="mt-1 flex items-center justify-between gap-3">
        <span className="text-[11px] text-[#959595]">
          {formatEarlyDeliveryByLabel(item.size.expectedDeliveryDate)}
        </span>
        <button
          type="button"
          disabled={isApplying}
          onClick={() => onViewDetails(item)}
          className="shrink-0 text-[13px] text-[var(--link-blue)] hover:underline disabled:opacity-50"
        >
          View Details
        </button>
      </div>
    </div>
  );
}

export function PdpEarlyDelivery({
  items,
  designId,
  service,
  onApplied,
}: PdpEarlyDeliveryProps) {
  const [expanded, setExpanded] = useState(false);
  const [isApplying, setIsApplying] = useState(false);

  const handleViewDetails = useCallback(
    async (item: EarlyDeliveryItem) => {
      setIsApplying(true);
      try {
        const selection = earlyDeliveryItemToSelection(item);
        const customization = await service.applyCustomization(designId, selection);
        const selectedSize: PdpSizeOption | null =
          item.size.size === '00'
            ? null
            : {
                key: item.size.size,
                value: item.size.displaySize,
              };
        onApplied({ customization, selectedSize, item });
      } finally {
        setIsApplying(false);
      }
    },
    [designId, onApplied, service]
  );

  if (items.length === 0) {
    return null;
  }

  const showAccordion = items.length > 3;

  return (
    <div className="relative mt-3 rounded border border-[#e3e3e3] bg-white">
      <div className="relative">
        <div
          className={cn(
            'overflow-hidden transition-[max-height] duration-300 ease-in-out',
            showAccordion && !expanded ? 'max-h-[210px]' : 'max-h-[1000px]'
          )}
        >
          <h3 className="border-b border-[#e3e3e3] bg-[#f7f7f7] px-[15px] py-2.5 text-sm font-semibold text-gray-900">
            Available for Delivery within {EARLY_DELIVERY_PERIOD_DAYS} days
          </h3>
          {items.map((item) => (
            <EarlyDeliveryItemRow
              key={item.id}
              item={item}
              isApplying={isApplying}
              onViewDetails={handleViewDetails}
            />
          ))}
        </div>

        {showAccordion && !expanded && (
          <div
            aria-hidden
            className="pointer-events-none absolute inset-x-0 bottom-0 h-[30px] bg-gradient-to-b from-white/0 via-[#f2f2f2]/40 to-[#f2f2f2]"
          />
        )}
      </div>

      {showAccordion && (
        <button
          type="button"
          aria-expanded={expanded}
          aria-label={expanded ? 'Show fewer delivery options' : 'Show more delivery options'}
          onClick={() => setExpanded((value) => !value)}
          className="flex w-full items-center justify-center border-t border-[#e3e3e3] bg-[#f2f2f2] py-1.5"
        >
          <span className="flex h-[25px] w-[25px] items-center justify-center rounded-full border-2 border-[#e2e2e2] bg-[#f2f2f2]">
            <ChevronDown
              className={cn('h-3.5 w-3.5 text-[#cccccc] transition-transform', expanded && 'rotate-180')}
              strokeWidth={2.5}
            />
          </span>
        </button>
      )}
    </div>
  );
}
